import { Components } from "@thisgorilla/primate-ds";
import moment from "moment";
import { useContext, useMemo, useState } from "react";
import styled, { useTheme } from "styled-components";

import { CreatePostModalContext } from "..";
import { API_CALLS } from "../../../../../../../API/api_calls";
import { Divider } from "../../../../../../../commonStyles";
import { useAppSelector } from "../../../../../../../redux";
import { isFormSubmitButtonDisabled } from "../../../../../../../utils";
import { EmojiPicker } from "../../../../../../Common";
import { ModalContext } from "../../../../../../Common/Modal";
import { FlexRowCenter } from "../../../../../../Common/Styles";
import { CreatePostModalProps } from "../CreatePostModal";
import Collaborator from "../ModalContent/Collaborator";
import { MAX_IMAGE_UPLOAD_COUNT } from "../ModalContent/ImageUpload/utils";

const ModalFooter = () => {
  const { typography, Color } = useTheme();
  const { uploadedFiles, post_id } = useAppSelector((state) => state.posts);
  const [showEmojiOptions, setShowEmojiOptions] = useState(false);
  const { footerRef } = useContext(ModalContext);
  const {
    setPollOpen,
    formik,
    pollOpen,
    choiceCount,
    setImageUploadOpen,
    setSchedulePostOpen,
    setSliderOpen,
    sliderOpen,
    isMoreImgUploadButtonDisable,
    setIsTopicOpen,
    setPostContentOrder,
    setIsAudioOpen,
    setIsVideoOpen,
    isAudioOpen,
    isVideoOpen,
  } = useContext(CreatePostModalContext);

  const { delivery_date } = formik.values;

  const footerLeftSideOptions: CreatePostModalProps.FooterLeftSideOption[] = [
    {
      iconType: "Image",
      onClick: () => {
        setImageUploadOpen(true);
        setPostContentOrder((prev) =>
          prev.includes("image") ? prev : [...prev, "image"]
        );
      },
      tooltipLabel: "Add media",
      disabled:
        isMoreImgUploadButtonDisable ||
        uploadedFiles.length === MAX_IMAGE_UPLOAD_COUNT,
    },
    {
      iconType: "VideoNew",
      onClick: () => {
        API_CALLS.POSTVIDEO.createVideoDraftForAPost({
          post_id,
        });
        setIsVideoOpen(true);
        setPostContentOrder((prev) =>
          prev.includes("video") ? prev : [...prev, "video"]
        );
      },
      tooltipLabel: "Add videos",
    },
    {
      iconType: "AudioNew",
      onClick: () => {
        setIsAudioOpen(true);
        API_CALLS.POSTAUDIO.createAudioDraftForAPost({
          post_id,
        });
        setPostContentOrder((prev) =>
          prev.includes("audio") ? prev : [...prev, "audio"]
        );
      },
      tooltipLabel: "Add audio",
    },
    {
      iconType: "Poll",
      onClick: () => {
        setPollOpen(true);
        setPostContentOrder((prev) =>
          prev.includes("poll") ? prev : [...prev, "poll"]
        );
      },
      tooltipLabel: "Create a poll",
    },
    {
      iconType: "Slider",
      onClick: () => {
        setSliderOpen(true);
        setPostContentOrder((prev) =>
          prev.includes("slider") ? prev : [...prev, "slider"]
        );
      },
      tooltipLabel: "Create a rating",
    },
    {
      iconType: "FileText",
      onClick: () => {},
      tooltipLabel: "Write an article",
    },
  ];

  const requiredFileds = useMemo(() => {
    const required: { [key: string]: boolean } = {};

    if (pollOpen) {
      required["question"] = true;
      required["duration"] = true;
      for (let i = 1; i <= choiceCount.length; i++) {
        required[`choice_${choiceCount[i - 1]}`] = true;
      }
    }

    if (sliderOpen) {
      required["slider_question"] = true;
      required["left_emoji_id"] = true;
      required["right_emoji_id"] = true;
    }

    if (isAudioOpen) {
      required["audio_description"] = true;
      required["audio_title"] = true;
    }

    if (isVideoOpen) {
      required["video_description"] = true;
      required["video_title"] = true;
    }

    required["content"] = !(
      sliderOpen ||
      pollOpen ||
      uploadedFiles.length ||
      isAudioOpen ||
      isVideoOpen
    );

    return required;
  }, [
    choiceCount,
    pollOpen,
    sliderOpen,
    uploadedFiles,
    isAudioOpen,
    isVideoOpen,
  ]);

  return (
    <Container ref={footerRef}>
      <Collaborator />
      <Divider />
      <ModalFooterContainer>
        <FooterLeftSide>
          {footerLeftSideOptions
            .slice(0, 3)
            .map(
              (
                { iconType, onClick, tooltipLabel, disabled = false },
                index
              ) => (
                <Components.Tooltip
                  type="label"
                  label={tooltipLabel}
                  position="top"
                  key={index}
                >
                  <Components.IconButton
                    iconType={iconType}
                    onClick={onClick}
                    disabled={disabled}
                    kind="Secondary"
                    size="Medium"
                  />
                </Components.Tooltip>
              )
            )}
          <EmojiPicker
            scrollAreaId={""}
            setShowEmojiOptions={setShowEmojiOptions}
            showEmojiOptions={showEmojiOptions}
            onGifSelected={(gif) => {
              setPostContentOrder((prev) =>
                prev.includes("gif") ? prev : [...prev, "gif"]
              );
              setShowEmojiOptions(false);
              API_CALLS.POSTGIF.uploadGIFForAPost({
                post_id,
                gif_id: gif.id,
                name: gif.title,
                type: gif.type,
                url: gif.images.original.url,
              });
            }}
          >
            <Components.Tooltip type="label" label={"Add emoji"} position="top">
              <Components.IconButton
                iconType={"Sticker"}
                onClick={() => {
                  setShowEmojiOptions(true);
                }}
                disabled={false}
                kind="Secondary"
                size="Medium"
              />
            </Components.Tooltip>
          </EmojiPicker>
          {footerLeftSideOptions
            .slice(3)
            .map(
              (
                { iconType, onClick, tooltipLabel, disabled = false },
                index
              ) => (
                <Components.Tooltip
                  type="label"
                  label={tooltipLabel}
                  position="top"
                  key={index}
                >
                  <Components.IconButton
                    iconType={iconType}
                    onClick={onClick}
                    disabled={disabled}
                    kind="Secondary"
                    size="Medium"
                  />
                </Components.Tooltip>
              )
            )}
        </FooterLeftSide>

        <FooterRightSide>
          <Components.Button
            color="Neutral"
            kind="Secondary"
            disabled={false}
            label="Topics"
            onClick={() => {
              setIsTopicOpen(true);
              setPostContentOrder((prev) =>
                prev.includes("topic") ? prev : [...prev, "topic"]
              );
            }}
            size="Medium"
            iconPosition="left"
            iconType="PlusCircle"
          />
          {delivery_date ? (
            <Wrapper>
              <Components.IconButton
                iconType="Calendar"
                onClick={() => {
                  setSchedulePostOpen(true);
                }}
                disabled={false}
                kind="Secondary"
                size="Medium"
              />
              <StatusIndicatorContainer>
                <Components.Tooltip
                  type="component"
                  label={
                    <FlexRowCenter>
                      <Components.Text
                        renderInSingleLine={true}
                        style={{ tokenStyle: typography.Body.M }}
                        text={"Post scheduled for "}
                      />
                      <Components.Text
                        renderInSingleLine={true}
                        style={{ tokenStyle: typography.Body.M }}
                        text={moment(delivery_date).format("LLLL")}
                        color={Color.text["text-emphasize-brand"].value}
                      />
                    </FlexRowCenter>
                  }
                  position="top"
                  width={400}
                >
                  <Components.StatusIndicator
                    iconType="Check"
                    state="Positive"
                  />
                </Components.Tooltip>
              </StatusIndicatorContainer>
            </Wrapper>
          ) : (
            <Components.Tooltip
              type="label"
              label="Schedule post"
              position="top"
            >
              <Components.IconButton
                iconType="Calendar"
                onClick={() => setSchedulePostOpen(true)}
                disabled={false}
                kind="Secondary"
                size="Medium"
              />
            </Components.Tooltip>
          )}

          <Components.Button
            color="Primary"
            kind="Primary"
            label="Post"
            onClick={() => {
              formik.isValid && formik.submitForm();
            }}
            disabled={isFormSubmitButtonDisabled({
              formik,
              requiredFileds: requiredFileds,
            })}
            size="Medium"
          />
        </FooterRightSide>
      </ModalFooterContainer>
    </Container>
  );
};

export default ModalFooter;

const Container = styled.div`
  position: absolute;
  bottom: ${({ theme }) => theme.spacing["spacing-none"].value}rem;
  right: ${({ theme }) => theme.spacing["spacing-none"].value}rem;
  left: ${({ theme }) => theme.spacing["spacing-none"].value}rem;
  z-index: 2;
  background: ${({ theme }) =>
    theme.Color.elevation.surface["surface-overlay"].value};
  padding: ${({ theme }) => theme.spacing["spacing-2"].value}rem
    ${({ theme }) => theme.spacing["spacing-4"].value}rem
    ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  border-radius: ${({ theme }) => theme.radius["radius-none"].value}rem
    ${({ theme }) => theme.radius["radius-none"].value}rem
    ${({ theme }) => theme.radius["radius-m"].value}rem
    ${({ theme }) => theme.radius["radius-m"].value}rem;
`;

const ModalFooterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  gap: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
  position: relative;
`;

const FooterLeftSide = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["spacing-2"].value}rem;
`;
const FooterRightSide = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing["spacing-4"].value}rem;
`;

const Wrapper = styled.div`
  position: relative;
`;
const StatusIndicatorContainer = styled.div`
  position: absolute;
  top: -${({ theme }) => theme.spacing["spacing-2"].value}rem;
  right: -${({ theme }) => theme.spacing["spacing-2"].value}rem;
`;
